/**
 * PlatformIssueDetail Component
 *
 * Platform-conditional component that renders either:
 * - Web: IssuePopover with positioning
 * - Native: IssueBottomSheet modal
 */

import React from 'react';
import { Platform } from 'react-native';
import type { InlineIssue, AnchorPosition } from './types';
import { IssuePopover } from './IssuePopover';
import { IssueBottomSheet } from './IssueBottomSheet';

export interface PlatformIssueDetailProps {
  /** All issues for navigation */
  issues: InlineIssue[];
  /** Active issue (web popover) */
  activeIssue: InlineIssue | null;
  /** Active issue index (web popover) */
  activeIssueIndex: number;
  /** Anchor position (web popover) */
  anchorPosition: AnchorPosition | null;
  /** Bottom sheet index (native) */
  bottomSheetIndex: number | null;
  /** Callback when accept button pressed */
  onAccept: (issueId: number) => void;
  /** Callback when ignore button pressed */
  onIgnore: (issueId: number) => void;
  /** Callback to close detail view */
  onClose: () => void;
  /** Callback when mouse enters popover (web) */
  onPopoverMouseEnter: () => void;
  /** Callback when mouse leaves popover (web) */
  onPopoverMouseLeave: () => void;
  /** Callback for previous navigation (web) */
  onWebPrevious: () => void;
  /** Callback for next navigation (web) */
  onWebNext: () => void;
  /** Callback for previous navigation (native) */
  onNativePrevious: () => void;
  /** Callback for next navigation (native) */
  onNativeNext: () => void;
  /** Optional test ID */
  testID?: string;
}

/**
 * PlatformIssueDetail - Platform-conditional issue detail view
 */
export function PlatformIssueDetail({
  issues,
  activeIssue,
  activeIssueIndex,
  anchorPosition,
  bottomSheetIndex,
  onAccept,
  onIgnore,
  onClose,
  onPopoverMouseEnter,
  onPopoverMouseLeave,
  onWebPrevious,
  onWebNext,
  onNativePrevious,
  onNativeNext,
  testID = 'platform-issue-detail',
}: PlatformIssueDetailProps) {
  return (
    <>
      {/* Web Popover */}
      {Platform.OS === 'web' && activeIssue && anchorPosition && (
        <IssuePopover
          issue={activeIssue}
          isVisible={!!activeIssue}
          anchorPosition={anchorPosition}
          onAccept={() => onAccept(activeIssue.id)}
          onIgnore={() => onIgnore(activeIssue.id)}
          onClose={onClose}
          onMouseEnter={onPopoverMouseEnter}
          onMouseLeave={onPopoverMouseLeave}
          // Navigation props for web parity with native
          issues={issues}
          currentIndex={activeIssueIndex}
          onPrevious={onWebPrevious}
          onNext={onWebNext}
          testID={`${testID}-popover`}
        />
      )}

      {/* Native Bottom Sheet */}
      {Platform.OS !== 'web' && bottomSheetIndex !== null && (
        <IssueBottomSheet
          issues={issues}
          currentIndex={bottomSheetIndex}
          isVisible={bottomSheetIndex !== null}
          onPrevious={onNativePrevious}
          onNext={onNativeNext}
          onAccept={() => onAccept(issues[bottomSheetIndex].id)}
          onIgnore={() => onIgnore(issues[bottomSheetIndex].id)}
          onClose={onClose}
          testID={`${testID}-bottomsheet`}
        />
      )}
    </>
  );
}
